import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ContractsService } from '../../../services/contracts.service';
import { ErrorService } from '../../../shared/services/error.service';
import { B2bContractDriver } from '../../../models/Contract';

@Component({
  selector: 'app-b2b-contracts-driver-form',
  templateUrl: './b2b-contracts-driver-form.component.html',
  styleUrls: ['./b2b-contracts-driver-form.component.css']
})
export class B2bContractsDriverFormComponent implements OnInit {
  driverForm: FormGroup;
  isLoading = false;
  isSubmitting = false;
  contractId: number | null = null;
  driverId: number | null = null;
  contractNumber: string = '';
  contractName: string = '';
  isEditMode = false;
  cnhDocumentUrl: string | null = null;
  termoResponsabilidadeUrl: string | null = null;


  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contractsService: ContractsService,
    private errorService: ErrorService,
    private fb: FormBuilder
  ) {
    this.driverForm = this.createForm();
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.contractId = +params['id'];
      this.driverId = params['driverId'] ? +params['driverId'] : null;
      
      if (this.contractId) {
        this.loadContractInfo();
        
        if (this.driverId) {
          this.isEditMode = true;
          this.loadDriver();
        }
      }
    });
  }

  createForm(): FormGroup {
    return this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      // Obrigatórios alinhados ao backend (B2BContractDriver): Name + DriverCpf
      driverCpf: ['', [Validators.required, Validators.pattern(/^\d{3}\.\d{3}\.\d{3}-\d{2}$|^\d{11}$/)]],
      // Demais campos opcionais (planilha/import frequentemente vêm incompletos)
      driverCnh: [''],
      cnhCategory: [''],
      cnhExpirationDate: [null],
      employeeRegistration: [''],
      admissionDate: [null],
      birthDate: [null],
      coordinatorName: [''],
      leaderName: [''],
      phone: [''],
      email: [''],
      address: [''],
      addressStreet: [''],
      addressNumber: [''],
      addressNeighborhood: [''],
      addressZipCode: [''],
      addressCity: [''],
      addressState: ['']
    });
  }

  loadContractInfo(): void {
    if (!this.contractId) return;

    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (contract) => {
        this.contractNumber = contract.contractNumber || '';
        this.contractName = contract.contractName || '';
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
      }
    });
  }

  loadDriver(): void {
    if (!this.contractId || !this.driverId) return;

    this.isLoading = true;

    this.contractsService.getB2bContractDriverById(this.contractId, this.driverId).subscribe({
      next: (driver) => {
        this.driverForm.patchValue({
          name: driver.name || '',
          driverCpf: this.normalizeCpf(driver.driverCpf || ''),
          driverCnh: this.onlyDigits(driver.driverCnh || ''),
          cnhCategory: driver.cnhCategory || '',
          cnhExpirationDate: this.toDateInput(driver.cnhExpirationDate),
          employeeRegistration: driver.employeeRegistration || '',
          admissionDate: this.toDateInput(driver.admissionDate),
          birthDate: this.toDateInput(driver.birthDate),
          coordinatorName: driver.coordinatorName || '',
          leaderName: driver.leaderName || '',
          phone: this.normalizePhone(driver.phone || ''),
          email: driver.email || '',
          address: driver.address || '',
          addressStreet: driver.addressStreet || '',
          addressNumber: driver.addressNumber || '',
          addressNeighborhood: driver.addressNeighborhood || '',
          addressZipCode: driver.addressZipCode || '',
          addressCity: driver.addressCity || '',
          addressState: driver.addressState || ''
        });
        this.cnhDocumentUrl = driver.cnhDocumentUrl || null;
        this.termoResponsabilidadeUrl = driver.termoResponsabilidadeUrl || null;
        this.driverForm.updateValueAndValidity();
        this.isLoading = false;
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
        this.isLoading = false;
      }
    });
  }

  onSubmit(): void {
    if (this.driverForm.invalid) {
      this.markFormGroupTouched(this.driverForm);
      const invalid = this.getInvalidFieldLabels();
      this.errorService.showErrorMessage(
        invalid.length
          ? `Corrija os campos: ${invalid.join(', ')}.`
          : 'Por favor, preencha os campos obrigatórios corretamente.'
      );
      return;
    }

    if (!this.contractId) {
      this.errorService.showErrorMessage('ID do contrato não encontrado.');
      return;
    }

    this.isSubmitting = true;

    const formValue = this.driverForm.value;
    
    // Preparar dados para envio (campos novos opcionais)
    const driverData = {
      name: formValue.name,
      driverCpf: formValue.driverCpf,
      driverCnh: formValue.driverCnh || null,
      cnhCategory: formValue.cnhCategory || null,
      cnhExpirationDate: formValue.cnhExpirationDate ? new Date(formValue.cnhExpirationDate).toISOString() : null,
      employeeRegistration: formValue.employeeRegistration || null,
      admissionDate: formValue.admissionDate ? new Date(formValue.admissionDate).toISOString() : null,
      birthDate: formValue.birthDate ? new Date(formValue.birthDate).toISOString() : null,
      coordinatorName: formValue.coordinatorName || null,
      leaderName: formValue.leaderName || null,
      phone: formValue.phone || null,
      email: formValue.email || null,
      address: formValue.address || null,
      addressStreet: formValue.addressStreet || null,
      addressNumber: formValue.addressNumber || null,
      addressNeighborhood: formValue.addressNeighborhood || null,
      addressZipCode: formValue.addressZipCode || null,
      addressCity: formValue.addressCity || null,
      addressState: formValue.addressState || null
    };

    if (this.isEditMode && this.driverId) {
      // Modo edição
      this.contractsService.updateB2bContractDriver(this.contractId, this.driverId, driverData).subscribe({
        next: (response) => {
          this.errorService.showSuccessMessage('Condutor atualizado com sucesso!');
          this.navigateBack();
        },
        error: (err) => {
          this.errorService.handleHttpError(err);
          this.isSubmitting = false;
        }
      });
    } else {
      // Modo criação
      this.contractsService.createB2bContractDriver(this.contractId, driverData).subscribe({
        next: (response) => {
          const created = response?.data || response?.Data || response;
          const newId = created?.id ?? created?.Id;
          this.isSubmitting = false;
          if (newId) {
            this.router.navigate(['/b2b/contracts', this.contractId, 'drivers', newId, 'edit']);
            return;
          }
          this.navigateBack();
        },
        error: (err) => {
          this.errorService.handleHttpError(err);
          this.isSubmitting = false;
        }
      });
    }
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  navigateBack(): void {
    if (this.contractId) {
      this.router.navigate(['/b2b/contracts', this.contractId, 'drivers']);
    } else {
      this.router.navigate(['/b2b/contracts']);
    }
  }

  onDocumentsUpdated(driver: B2bContractDriver): void {
    this.cnhDocumentUrl = driver.cnhDocumentUrl || null;
    this.termoResponsabilidadeUrl = driver.termoResponsabilidadeUrl || null;
  }

  formatCpf(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    
    if (value.length <= 11) {
      if (value.length <= 3) {
        value = value;
      } else if (value.length <= 6) {
        value = value.replace(/^(\d{3})(\d+)$/, '$1.$2');
      } else if (value.length <= 9) {
        value = value.replace(/^(\d{3})(\d{3})(\d+)$/, '$1.$2.$3');
      } else {
        value = value.replace(/^(\d{3})(\d{3})(\d{3})(\d+)$/, '$1.$2.$3-$4');
      }
      
      this.driverForm.patchValue({ driverCpf: value }, { emitEvent: false });
    }
  }

  formatPhone(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    
    if (value.length <= 11) {
      if (value.length <= 2) {
        value = value;
      } else if (value.length <= 6) {
        value = value.replace(/^(\d{2})(\d+)$/, '($1) $2');
      } else if (value.length <= 10) {
        value = value.replace(/^(\d{2})(\d{4})(\d+)$/, '($1) $2-$3');
      } else {
        value = value.replace(/^(\d{2})(\d{5})(\d+)$/, '($1) $2-$3');
      }
      
      this.driverForm.patchValue({ phone: value }, { emitEvent: false });
    }
  }

  formatCnh(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    
    // CNH brasileira tem 11 dígitos
    if (value.length <= 11) {
      this.driverForm.patchValue({ driverCnh: value }, { emitEvent: false });
    }
  }

  private toDateInput(value: string | null | undefined): string | null {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10);
  }

  private onlyDigits(value: string): string {
    return String(value || '').replace(/\D/g, '');
  }

  private normalizeCpf(value: string): string {
    const digits = this.onlyDigits(value).slice(0, 11);
    if (digits.length !== 11) return value || '';
    return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  }

  private normalizePhone(value: string): string {
    const digits = this.onlyDigits(value).slice(0, 11);
    if (digits.length === 10) {
      return digits.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    }
    if (digits.length === 11) {
      return digits.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
    }
    return value || '';
  }

  private getInvalidFieldLabels(): string[] {
    const labels: Record<string, string> = {
      name: 'Nome',
      driverCpf: 'CPF',
      driverCnh: 'CNH',
      phone: 'Telefone',
      email: 'Email'
    };
    return Object.keys(this.driverForm.controls)
      .filter((key) => this.driverForm.get(key)?.invalid)
      .map((key) => labels[key] || key);
  }

  onCpfBlur(): void {
    const cpfControl = this.driverForm.get('driverCpf');
    if (cpfControl?.value) {
      let value = cpfControl.value.replace(/\D/g, '');
      if (value.length === 11) {
        value = value.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
        cpfControl.setValue(value, { emitEvent: false });
      }
    }
  }

  onPhoneBlur(): void {
    const phoneControl = this.driverForm.get('phone');
    if (phoneControl?.value) {
      let value = phoneControl.value.replace(/\D/g, '');
      if (value.length === 10) {
        value = value.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
        phoneControl.setValue(value, { emitEvent: false });
      } else if (value.length === 11) {
        value = value.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
        phoneControl.setValue(value, { emitEvent: false });
      }
    }
  }
}
